// @flow

/* eslint-disable max-params */
import { createSelector } from 'reselect';

import { FacetFilters } from 'Utils/faceting/facetingFilters';
import { getChildcareFacets } from 'Utils/faceting/facetingUtils';
import {
  ACTIVITIES_FACET_ID,
  ADULTS_ONLY_FACET_ID,
  CHILDCARE_FACET_ID,
  DEPARTURE_CITY_FACET_ID,
  FILTERABLE_FACETS_IDS,
} from 'Utils/faceting/constants';
import {
  getDepartureDate,
  getDepartureCity,
  getTripDuration,
} from 'Store/trip-criteria/tripCriteria.selector';

import { getKids } from 'Store/trip-information/storeHelper';

const facetingSelector = ({ faceting }: ReduxState) => faceting || {};
const activeFacetsSelector = createSelector(
  facetingSelector,
  ({ activeFacets }) => activeFacets || {},
);
const searchQuerySelector = createSelector(facetingSelector, ({ query }) => query || { label: '' });
const facetsSelector = createSelector(facetingSelector, ({ facets }) => facets || []);
const filtersSelector = createSelector(facetingSelector, ({ filters }) => filters || []);
const facetingPricesSelector = createSelector(facetingSelector, ({ prices }) => prices);
const filteredProductsSelector: (state: ReduxState) => Array<Resort> = createSelector(
  facetingSelector,
  ({ products = [] }) => products,
);
const recommandedProductsSelector: (state: ReduxState) => Array<Resort> = createSelector(
  facetingSelector,
  ({ recommandedProducts = [] }) => recommandedProducts,
);
const bannersSelector = createSelector(facetingSelector, ({ banners }) => banners || []);
const filteredProductsCountSelector = createSelector(
  facetingSelector,
  ({ products = [] }) => products.length,
);
const filteredProductsSeasonsSelector = createSelector(filteredProductsSelector, (products = []) =>
  products.reduce(
    (seasons, product) =>
      seasons.includes(product.seasonName) ? seasons : [...seasons, product.seasonName],
    [],
  ),
);
const getFacetingProductIds = createSelector(filteredProductsSelector, (products = []) =>
  products.map(({ id }) => id),
);

const getKidsValidationState = createSelector(
  facetingSelector,
  ({ kidsValidationState = [] }) => kidsValidationState,
);

/**
 * Returns an array of objects [{id: <filter_name>, name: <filter_value>}]
 * representing active facets to send to Pertimm.
 *
 * Example:
 * ```json
 * [
 *   { id: 'activities_tree', name: 'Sports Terrestres|Ski' }
 * ]
 * ```
 */
const activeFacetsCriteriaSelector = createSelector(activeFacetsSelector, activeFacets => {
  return Object.keys(activeFacets).reduce((acc, categoryId) => {
    Object.entries(activeFacets[categoryId]).map(([filter, isSelected]) => {
      if (isSelected) {
        acc.push({ id: categoryId, name: filter });
      }
    });
    return acc;
  }, []);
});

/**
 * Returns an array of objects [{id: <filter_name>, name: <filter_value>}]
 * containing aggregation of both facets and criterias to send to Pertimm.
 *
 * Example:
 * ```json
 * [
 *   { id: 'facette_adult_only', name: false },
 *   { id: 'activities_tree', name: 'Sports Terrestres|Ski' }
 * ]
 * ```
 */
const getFiltersSelector = createSelector(
  getKids,
  getDepartureDate,
  activeFacetsCriteriaSelector,
  filtersSelector,
  getDepartureCity,
  facetsSelector,
  (kids, departureDate, activeFacets, filters, departureCity, facets) => {
    const allFilters = new FacetFilters(filters)
      .removeFilters([
        ACTIVITIES_FACET_ID,
        ADULTS_ONLY_FACET_ID,
        CHILDCARE_FACET_ID,
        DEPARTURE_CITY_FACET_ID,
        ...FILTERABLE_FACETS_IDS,
      ])
      .addAdultOnlyFilter(kids)
      .addChildcareFilters({ kids, childcareFacets: getChildcareFacets(facets), departureDate })
      .addFilters(activeFacets);

    if (departureCity) {
      allFilters.addFilter({ id: DEPARTURE_CITY_FACET_ID, name: departureCity });
    }

    return allFilters.value();
  },
);

/**
 * Returns an object with an aggregation of criterias for Pertimm:
 *
 * Example:
 * ```json
 * {
 *    departureDate: '',
 *    tripDuration: '7',
 *    filters: [
 *      { id: 'facette_departure_city',  name: 'PAR' },
 *      { id: 'activities_tree', name: 'Sports Terrestres|Ski' }
 *    ]
 * }
 * ```
 */

const facetingCriteriaSelector = createSelector(
  [getDepartureDate, getTripDuration, getFiltersSelector, getKids],
  (departureDate, tripDuration, filters, kids) => ({
    departureDate,
    tripDuration,
    filters,
    kids,
  }),
);

export {
  activeFacetsSelector,
  bannersSelector,
  facetingCriteriaSelector,
  facetingSelector,
  facetingPricesSelector,
  facetsSelector,
  filteredProductsSelector,
  recommandedProductsSelector,
  filteredProductsCountSelector,
  filteredProductsSeasonsSelector,
  filtersSelector,
  getFacetingProductIds,
  getFiltersSelector,
  getKidsValidationState,
  searchQuerySelector,
};
